import type { ReactNode } from 'react'
import { mutate as globalMutate } from 'swr'
import useUser from 'shared/hooks/use-user'
import type { DomainAssetsInterface } from 'shared/types/domain'
import { baseServerSideProps } from 'shared/utils/base-server-side-props'
import { withDomainAssets } from 'shared/utils/with-domain-assets'
import CommunityCart from 'modules/community/components/community-cart'
import { useCommunities } from 'modules/community/hooks/use-communities'
import { useLeaveCommunity } from 'modules/community/hooks/use-leave-community'
import { getCommunityPageLayout } from 'modules/community/layouts/get-community-page-layout'
import type { CommunityInterface } from 'modules/community/types/community-interface'

const Communities = (): ReactNode => {
  const { loading } = useUser()
  const { data: communities = [], mutate } = useCommunities()
  const { leaveCommunity, isFetching } = useLeaveCommunity()

  const onLeaveCommunity = async (community: CommunityInterface): Promise<void> => {
    await leaveCommunity(community.path)
    await globalMutate(`/api/community/community/${community.path}/member`)
    await mutate(data => data?.filter(item => item.id !== community.id))
  }

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array.from({ length: 6 }).keys()].map(index => (
          <SkeletonCommunityItem key={index} />
        ))}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
      {communities.map(community => (
        <CommunityCart
          key={`${community.name}-${community.id}`}
          community={community}
          isFetching={isFetching}
          onLeaveCommunity={onLeaveCommunity}
        />
      ))}
    </div>
  )
}

const SkeletonCommunityItem = (): ReactNode => {
  return (
    <div
      className="w-full max-w-115 animate-pulse overflow-hidden rounded-lg bg-background-secondary"
      key="skeleton-community-1"
    >
      <div className="mb-5 h-45 bg-skeleton-primary" />
      <div className="px-5 py-6">
        <div className="mb-5 h-4 w-36 rounded-xs bg-skeleton-primary" />
        {[...Array.from({ length: 3 }).keys()].map(index => (
          <div className="mb-3 h-3 rounded-xs bg-skeleton-primary" key={index} />
        ))}
        <div className="float-right my-3 h-6 w-20 rounded-lg bg-skeleton-primary" />
      </div>
    </div>
  )
}

export const getServerSideProps = withDomainAssets(baseServerSideProps)

Communities.getLayout = (page: ReactNode, props: { domainAssets?: DomainAssetsInterface }) =>
  getCommunityPageLayout(page, {
    titleKey: 'communities.page_title',
    domainAssets: props.domainAssets,
  })

export default Communities
